if (!customElements.get('quick-filter')) {
  class QuickFilter extends HTMLElement {

    constructor() {
      super();
      this.select1 = this.querySelector('.js-filter-select-1');
      this.select2 = this.querySelector('.js-filter-select-2');
      this.select3 = this.querySelector('.js-filter-select-3');
      this.button = this.querySelector('.js-filter-submit');
      this.minSearchItems = Number(this.dataset.searchMinItems);
      // Remove debounce for immediate initialization
      this.init = this.doInit.bind(this);

      this.handleSelectChange = this.handleSelectChange.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);

      // Handle Shopify section events for theme editor
      if (Shopify && Shopify.designMode) {
        document.addEventListener('shopify:section:load', (evt) => {
          if (evt.target.contains(this) || evt.target === this || evt.target.querySelector('quick-filter') === this) {
            this.destroy();
            // Small delay to ensure DOM is ready
            requestAnimationFrame(() => {
              this.init();
            });
          }
        });

        document.addEventListener('shopify:section:reorder', (evt) => {
          if (evt.target.contains(this)) {
            this.destroy();
            requestAnimationFrame(() => {
              this.init();
            });
          }
        });
      }
    }

    connectedCallback() {
      if (window.sectionInstances.has(this)) {
        return; // Already initialized, ignore
      }

      window.sectionInstances.set(this, true);
      
      // Initialize immediately - no delays for Quick filter
      // Always initialize immediately for instant interactivity
      const initNow = () => {
        // Use microtask to ensure DOM is fully ready
        Promise.resolve().then(() => {
          this.init();
        });
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initNow);
      } else {
        // DOM already ready, initialize immediately
        initNow();
      }
    }

    disconnectedCallback() {
      this.destroy();
    }

    destroy() {
      try { this.niceSelect1?.destroy(); } catch (err) {}
      try { this.niceSelect2?.destroy(); } catch (err) {}
      try { this.niceSelect3?.destroy(); } catch (err) {}

      this.querySelectorAll('.nice-select').forEach(select => select.remove());

      // Remove NiceSelect styles
      this.select1?.removeAttribute('style');
      this.select2?.removeAttribute('style');
      this.select3?.removeAttribute('style');

      // Remove event listeners for change events
      this.select1.removeEventListener('change', this.handleSelectChange);
      this.select2?.removeEventListener('change', this.handleSelectChange);
      this.select3?.removeEventListener('change', this.handleSelectChange);
      this.button.removeEventListener('click', this.handleSubmit);
    }

    doInit() {
      // Add event listeners for change events
      this.select1.addEventListener('change', this.handleSelectChange);
      this.select2?.addEventListener('change', this.handleSelectChange);
      this.select3?.addEventListener('change', this.handleSelectChange);
      this.button.addEventListener('click', this.handleSubmit);

      // Preselect the dropdown values
      if (this.dataset.preselect) {
        let select1Index = 0;
        let select2Index = 0;
        let select3Index = 0;

        function isMatchingUrl(optionUrl) {
          const pageUrl = window.location.href;
          const pageUrlWithoutFirstPath = new URL(window.location.href).href.replace(new URL(window.location.href).origin + '/' + window.location.pathname.split('/')[1], new URL(window.location.href).origin);
          const pageUrlWithoutDomain = window.location.pathname + window.location.search;
          const pageUrlWithoutDomainAndFirstPath = pageUrlWithoutDomain.replace(/^\/[^/]+/, '');

          // Function to decode and normalize URLs (handle spaces and encoded characters)
          const normalizeUrl = (url) => {
            return decodeURIComponent(url)  // Decode any percent-encoded characters
              .replace(/\+/g, ' ')         // Normalize spaces encoded as "+" to " "
              .replace(/%20/g, ' ');       // Replace "%20" (space) with " " for uniformity
          };

          // Normalize all URLs
          const normalizedPageUrl = normalizeUrl(pageUrl);
          const normalizedPageUrlWithoutDomain = normalizeUrl(pageUrlWithoutDomain);
          const normalizedPageUrlWithoutFirstPath = normalizeUrl(pageUrlWithoutFirstPath);
          const normalizedPageUrlWithoutDomainAndFirstPath = normalizeUrl(pageUrlWithoutDomainAndFirstPath);
          const normalizedOptionUrl = normalizeUrl(optionUrl);

          // Compare normalized URLs
          return normalizedOptionUrl === normalizedPageUrl ||
            normalizedOptionUrl === normalizedPageUrlWithoutDomain ||
            normalizedOptionUrl === normalizedPageUrlWithoutFirstPath ||
            normalizedOptionUrl === normalizedPageUrlWithoutDomainAndFirstPath;
        }

        // Check for matches in the third select
        if (this.select3) {
          for (const option of this.select3.options) {
            if (isMatchingUrl(option.dataset.url)) {
              // If there's a match with URL or pathname, prioritize it
              select3Index = option.index;
              break;
            }
          }

          // If no URL/pathname match is found, check for the preselect match
          if (select3Index === 0) {
            for (const option of this.select3.options) {
              if (option.dataset.url === this.dataset.preselect) {
                select3Index = option.index;
                break;
              }
            }
          }
        }

        // Sort out select2
        if (select3Index === 0 && this.select2) {
          // Check for matches in the second select (URL and pathname)
          for (const option of this.select2.options) {
            if (isMatchingUrl(option.dataset.url)) {
              // If a match is found with the URL or pathname, prioritize it
              select2Index = option.index;
              break;
            }
          }

          // If no URL/pathname match is found, check for the preselect match
          if (select2Index === 0) {
            for (const option of this.select2.options) {
              if (option.dataset.url === this.dataset.preselect) {
                select2Index = option.index;
                break;
              }
            }
          }
        } else {
          const parentId = this.select3.options[select3Index].dataset.parentId;
          const option = Array.from(this.select2.options).find(option => option.id === parentId);
          select2Index = option.index;
        }

        // Sort out select1
        if (select2Index === 0) {
          for (const option of this.select1.options) {
            if (option.dataset.url === this.dataset.preselect) {
              select1Index = option.index;
              break;
            }
          }
        } else {
          const parentId = this.select2.options[select2Index].dataset.parentId;
          const option = Array.from(this.select1.options).find(option => option.id === parentId);
          select1Index = option.index;
        }

        // Update the dropdown selections
        const updateSelection = (select, index) => {
          const options = Array.from(select.options);
          options.forEach(option => option.removeAttribute('selected')); // Deselect all options
          if (options[index]) {
            options[index].setAttribute('selected', 'selected'); // Select the correct option
          }
        };

        updateSelection(this.select1, select1Index);
        this.select1.dispatchEvent(new Event('change'));

        if (this.select2) {
          updateSelection(this.select2, select2Index);
          this.select2.dispatchEvent(new Event('change'));
        }

        if (this.select3) {
          updateSelection(this.select3, select3Index);
          this.select3.dispatchEvent(new Event('change'));
        }
      }

      // Initialize NiceSelect immediately (no requestAnimationFrame delay)
      // Use microtask to ensure DOM is ready
      Promise.resolve().then(() => {
        if (this.select1) {
          this.niceSelect1 = NiceSelect.bind(this.select1, {
            searchable: this.minSearchItems > 0 && this.select1.options.length > this.minSearchItems,
            searchtext: window.accessibilityStrings?.search || 'Search'
          });
          // Set up handlers after a brief delay to ensure NiceSelect has created the dropdown
          setTimeout(() => {
            this.setupNiceSelectClickHandlers(this.select1, this.niceSelect1);
          }, 10);
        }

        if (this.select2) {
          this.niceSelect2 = NiceSelect.bind(this.select2, {
            searchable: this.minSearchItems > 0,
            searchtext: window.accessibilityStrings?.search || 'Search'
          });
          setTimeout(() => {
            this.setupNiceSelectClickHandlers(this.select2, this.niceSelect2);
          }, 10);
        }

        if (this.select3) {
          this.niceSelect3 = NiceSelect.bind(this.select3, {
            searchable: this.minSearchItems > 0,
            searchtext: window.accessibilityStrings?.search || 'Search'
          });
          setTimeout(() => {
            this.setupNiceSelectClickHandlers(this.select3, this.niceSelect3);
          }, 10);
        }
      });
    }

    /**
     * Setup click handlers to prevent navigation on dropdown options
     * Only the search button should navigate - dropdown selections only update filter state
     */
    setupNiceSelectClickHandlers(select, niceSelect) {
      if (!select || !niceSelect) return;

      const selectContainer = select.closest('.select');
      if (!selectContainer) return;

      // Block clicks from passing through the dropdown to elements behind
      // Use CSS (z-index, pointer-events) instead of JS to avoid blocking NiceSelect
      const setupDropdownBlocking = () => {
        const niceSelectElement = selectContainer.querySelector('.nice-select');
        if (!niceSelectElement) return;

        const dropdown = niceSelectElement.querySelector('.nice-select-dropdown');
        if (!dropdown) return;

        // Use CSS to block clicks from passing through - don't interfere with NiceSelect's JS
        dropdown.style.pointerEvents = 'auto';
        dropdown.style.zIndex = '200';
        dropdown.style.position = 'absolute';
        
        // Lower z-index for other (closed) selects in the same quick-filter
        const quickFilter = select.closest('quick-filter');
        if (quickFilter) {
          // Lower z-index for all closed selects
          quickFilter.querySelectorAll('.select:not(:has(.nice-select.open))').forEach(closedSelect => {
            closedSelect.style.zIndex = '1';
          });
          // Lower z-index for all closed select containers (including 3rd one)
          quickFilter.querySelectorAll('.quick-filter__select-container:not(:has(.nice-select.open))').forEach(closedContainer => {
            closedContainer.style.zIndex = '1';
            // Also lower z-index for all selects inside closed containers
            closedContainer.querySelectorAll('.select').forEach(closedSelect => {
              closedSelect.style.zIndex = '1';
            });
            // Also lower z-index for select elements themselves
            closedContainer.querySelectorAll('.select__select, .nice-select').forEach(selectElement => {
              selectElement.style.zIndex = '1';
            });
          });
          // Ensure the open select container has high z-index
          selectContainer.style.zIndex = '200';
          // Ensure the open select container's parent also has high z-index
          const selectContainerParent = selectContainer.closest('.quick-filter__select-container');
          if (selectContainerParent) {
            selectContainerParent.style.zIndex = '200';
          }
        }
        
        // Force solid background on mobile - prevent transparency
        const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--color-background').trim();
        if (bgColor) {
          dropdown.style.backgroundColor = `rgb(${bgColor})`;
          dropdown.style.background = `rgb(${bgColor})`;
        } else {
          // Fallback to white if CSS variable not available
          dropdown.style.backgroundColor = '#ffffff';
          dropdown.style.background = '#ffffff';
        }
        dropdown.style.opacity = '1';
        dropdown.style.backdropFilter = 'none';
        dropdown.style.webkitBackdropFilter = 'none';
        dropdown.style.backgroundImage = 'none';
        
        // Also ensure nice-list has background
        const niceList = dropdown.querySelector('.nice-list');
        if (niceList) {
          if (bgColor) {
            niceList.style.backgroundColor = `rgb(${bgColor})`;
            niceList.style.background = `rgb(${bgColor})`;
          } else {
            niceList.style.backgroundColor = '#ffffff';
            niceList.style.background = '#ffffff';
          }
          niceList.style.opacity = '1';
          niceList.style.backgroundImage = 'none';
        }
      };

      // Set up dropdown blocking
      setupDropdownBlocking();
      setTimeout(setupDropdownBlocking, 50);
      setTimeout(setupDropdownBlocking, 200);
      
      // Also set up when NiceSelect updates
      if (niceSelect && typeof niceSelect.update === 'function') {
        const originalUpdate = niceSelect.update.bind(niceSelect);
        niceSelect.update = () => {
          originalUpdate();
          setTimeout(setupDropdownBlocking, 50);
        };
      }
    }

    /**
     * Check if an option has children in the next dropdown
     */
    optionHasChildren(select, option) {
      if (!option || !option.id) return false;

      // Check if this option has children in the next dropdown
      const nextSelect = select === this.select1 ? this.select2 : 
                        select === this.select2 ? this.select3 : null;
      
      if (!nextSelect) return false;

      // Check if any option in the next select has this option's id as parentId
      for (let i = 1; i < nextSelect.options.length; i++) {
        const nextOption = nextSelect.options[i];
        if (nextOption.dataset.parentId === option.id) {
          return true;
        }
      }
      
      return false;
    }

    /**
     * Manually update NiceSelect rendered options to match filtered native select options
     * This ensures hidden/disabled options are properly hidden in the NiceSelect dropdown
     */
    updateNiceSelectOptions(select, niceSelect) {
      if (!select || !niceSelect) return;

      const selectContainer = select.closest('.select');
      if (!selectContainer) return;

      const niceSelectElement = selectContainer.querySelector('.nice-select');
      if (!niceSelectElement) return;

      const dropdown = niceSelectElement.querySelector('.nice-select-dropdown');
      if (!dropdown) return;

      const optionList = dropdown.querySelectorAll('.option');
      
      // Create a map of native option values and IDs to their hidden/disabled state
      const nativeOptionsMap = new Map();
      Array.from(select.options).slice(1).forEach((nativeOption) => {
        const optionValue = nativeOption.value;
        const optionId = nativeOption.id;
        const optionText = nativeOption.textContent.trim();
        const isHidden = nativeOption.hasAttribute('hidden') || nativeOption.hasAttribute('disabled');
        
        // Map by value, ID, and text for maximum compatibility
        nativeOptionsMap.set(optionValue, { isHidden, id: optionId, text: optionText });
        if (optionId) nativeOptionsMap.set(optionId, { isHidden, id: optionId, text: optionText });
        nativeOptionsMap.set(optionText, { isHidden, id: optionId, text: optionText });
      });
      
      // Update each rendered option by matching with native select option
      optionList.forEach((renderedOption, index) => {
        // Skip placeholder (first option)
        if (index === 0) return;
        
        // Try multiple ways to match the option
        const optionData = renderedOption.dataset.value || renderedOption.getAttribute('data-value');
        const optionId = renderedOption.id || renderedOption.getAttribute('id');
        const optionText = renderedOption.textContent.trim();
        
        let isHidden = true; // Default to hidden if not found
        let matched = false;
        
        // Try to match by value first
        if (optionData && nativeOptionsMap.has(optionData)) {
          isHidden = nativeOptionsMap.get(optionData).isHidden;
          matched = true;
        }
        // Then try by ID
        else if (optionId && nativeOptionsMap.has(optionId)) {
          isHidden = nativeOptionsMap.get(optionId).isHidden;
          matched = true;
        }
        // Finally try by text content
        else if (optionText && nativeOptionsMap.has(optionText)) {
          isHidden = nativeOptionsMap.get(optionText).isHidden;
          matched = true;
        }
        
        // If still not matched, check native options directly
        if (!matched) {
          for (let i = 1; i < select.options.length; i++) {
            const nativeOption = select.options[i];
            if (nativeOption.textContent.trim() === optionText || 
                nativeOption.value === optionData ||
                nativeOption.id === optionId) {
              isHidden = nativeOption.hasAttribute('hidden') || nativeOption.hasAttribute('disabled');
              break;
            }
          }
        }
        
        // Update the rendered option visibility
        if (isHidden) {
          renderedOption.style.display = 'none';
          renderedOption.classList.add('disabled');
          renderedOption.setAttribute('aria-disabled', 'true');
          renderedOption.style.pointerEvents = 'none';
          renderedOption.style.cursor = 'default';
        } else {
          renderedOption.style.display = '';
          renderedOption.classList.remove('disabled');
          renderedOption.removeAttribute('aria-disabled');
          renderedOption.style.pointerEvents = '';
          renderedOption.style.cursor = 'pointer';
        }
      });
    }

    handleSubmit() {
      let url = null;
      let selectedOption = null;

      // Navigate to the deepest selected level (generation > series > brand)
      if (this.select3?.selectedIndex > 0) {
        selectedOption = this.select3.options[this.select3.selectedIndex];
        if (selectedOption.dataset.url && selectedOption.dataset.url !== '#') {
          url = selectedOption.dataset.url;
        }
      } else if (this.select2?.selectedIndex > 0) {
        selectedOption = this.select2.options[this.select2.selectedIndex];
        if (selectedOption.dataset.url && selectedOption.dataset.url !== '#') {
          url = selectedOption.dataset.url;
        }
      } else if (this.select1?.selectedIndex > 0) {
        selectedOption = this.select1.options[this.select1.selectedIndex];
        // Only navigate if it's a leaf node (no children)
        if (selectedOption.dataset.url && selectedOption.dataset.url !== '#' && !this.optionHasChildren(this.select1, selectedOption)) {
          url = selectedOption.dataset.url;
        }
      }

      if (url) {
        url = this.makeAbsoluteUrl(url);

        // List of Shopify first-level directories to exclude
        const shopifyDirectories = new Set([
          "products", "collections", "pages", "blogs", "cart", "checkout", "account", "search",
          "admin", "apps", "a", "challenge", "community", "discount", "gift_card",
          "s", "cdn", "metaobject", "policies", "orders", "services", "tools", "events", "news", "legal"
        ]);

        // Get the first path segment from the current URL
        const currentFirstPath = window.location.pathname.split('/')[1];

        // Get the first path segment from the given URL
        const urlFirstPath = new URL(url).pathname.split('/')[1];

        // Check if the first path segments are different and the currentFirstPath is not a Shopify directory
        if (currentFirstPath !== urlFirstPath && !shopifyDirectories.has(currentFirstPath)) {
          // Modify the URL by inserting the first path segment of the current URL
          const modifiedUrl = new URL(url);
          modifiedUrl.pathname = `${currentFirstPath}${modifiedUrl.pathname}`;

          // Redirect to the modified URL
          window.location.href = modifiedUrl.href;
        } else {
          // If the first path segments are the same or in the exclusion list, just set the URL
          window.location.href = url;
        }
      } else {
        // No valid URL found - log warning but don't throw
        console.warn('Quick filter: No valid URL to navigate to. Please select a final option.');
      }
    }

    makeAbsoluteUrl(url) {
      // Check if the URL contains the domain (i.e., it's already absolute)
      if (url.includes(window.location.origin)) {
        return url; // If it's already absolute, return as is
      }

      // If it's a relative path, prepend the domain
      const domain = window.location.origin;
      return domain + (url.startsWith('/') ? url : '/' + url);
    }

    handleSelectChange(event) {
      const currentSelect = event.target;
      const selectedOption = currentSelect.options[currentSelect.selectedIndex];
      
      if (!selectedOption || currentSelect.selectedIndex === 0) {
        // Placeholder selected or no selection - disable submit
        this.button.setAttribute('disabled', 'disabled');
        return;
      }

      // Check if selected option has children - if so, don't enable submit button yet
      // Only enable submit when a final selection (leaf node) is made
      const hasChildren = this.optionHasChildren(currentSelect, selectedOption);
      const hasValidUrl = selectedOption.dataset.url && selectedOption.dataset.url !== '#';
      const isLeafNode = !hasChildren && hasValidUrl;

      // Enable/disable the submit button based on whether we have a final selection
      // Submit is enabled if ANY dropdown has a valid selection (prefer deepest level)
      if (isLeafNode) {
        this.button.removeAttribute('disabled');
      } else {
        // Check if we have any valid selection in any dropdown
        let hasAnyValidSelection = false;
        
        // Check select3 first (deepest)
        if (this.select3 && this.select3.selectedIndex > 0) {
          const option3 = this.select3.options[this.select3.selectedIndex];
          if (option3.dataset.url && option3.dataset.url !== '#') {
            hasAnyValidSelection = true;
          }
        }
        // Then check select2
        if (!hasAnyValidSelection && this.select2 && this.select2.selectedIndex > 0) {
          const option2 = this.select2.options[this.select2.selectedIndex];
          if (option2.dataset.url && option2.dataset.url !== '#') {
            hasAnyValidSelection = true;
          }
        }
        // Finally check select1
        if (!hasAnyValidSelection && this.select1 && this.select1.selectedIndex > 0) {
          const option1 = this.select1.options[this.select1.selectedIndex];
          if (option1.dataset.url && option1.dataset.url !== '#' && !this.optionHasChildren(this.select1, option1)) {
            hasAnyValidSelection = true;
          }
        }
        
        if (hasAnyValidSelection) {
          this.button.removeAttribute('disabled');
        } else {
          this.button.setAttribute('disabled', 'disabled');
        }
      }

      const nextSelect =
        currentSelect === this.select1 ? this.select2 :
          currentSelect === this.select2 ? this.select3 : null;
      if (!nextSelect) return;

      // Prepare the next select
      const options = Array.from(nextSelect.options);
      let optionsShown = 0;

      // Iterate over each <option> in the next select and toggle visibility
      // This filters options based on parent-child relationship
      options.slice(1).forEach(option => {
        if (option.dataset.parentId === selectedOption.id) {
          optionsShown++;
          option.removeAttribute('disabled');
          option.removeAttribute('hidden');
          option.style.display = '';
        } else {
          option.setAttribute('hidden', 'hidden');
          option.setAttribute('disabled', 'disabled');
          option.style.display = 'none';
        }
      });

      // Show/hide the search bar
      nextSelect.closest('.quick-filter__select-container').classList.toggle(
        'quick-filter__hide-search', optionsShown < this.minSearchItems);

      // Open the next select automatically if enabled
      if (this.dataset.autoOpenNextDropdown && optionsShown > 0) {
        requestAnimationFrame(() => {
          if (nextSelect === this.select2) {
            const niceSelect2 = this.querySelector('.nice-select.js-filter-select-2');
            if (niceSelect2 && !niceSelect2.classList.contains('open')) {
              niceSelect2.click();
            }
          }
          if (nextSelect === this.select3) {
            const niceSelect3 = this.querySelector('.nice-select.js-filter-select-3');
            if (niceSelect3 && !niceSelect3.classList.contains('open')) {
              niceSelect3.click();
            }
          }
        });
      }

      // Enable or disable the next select based on visible options
      if (optionsShown > 0) {
        nextSelect.removeAttribute('disabled');
      } else {
        nextSelect.setAttribute('disabled', 'disabled');
      }

      // Reset deeper selects when a parent selection changes
      nextSelect.querySelector('option').setAttribute('selected', 'selected');
      if (this.select2 && nextSelect === this.select2) {
        // Reset select3 when select2 changes
        if (this.select3) {
          this.select3.querySelector('option').setAttribute('selected', 'selected');
          this.select3.setAttribute('disabled', 'disabled');
          // Hide all select3 options
          Array.from(this.select3.options).slice(1).forEach(option => {
            option.setAttribute('hidden', 'hidden');
            option.setAttribute('disabled', 'disabled');
            option.style.display = 'none';
          });
        }
      }

      // Update NiceSelect to reflect the filtered options
      // Use requestAnimationFrame to ensure DOM updates are complete
      requestAnimationFrame(() => {
        if (this.select2 && nextSelect === this.select2) {
          try {
            this.niceSelect2?.update();
            // Manually update the rendered options immediately
            this.updateNiceSelectOptions(this.select2, this.niceSelect2);
            // Re-setup click handlers after update (NiceSelect may have rebuilt the dropdown)
            setTimeout(() => {
              this.setupNiceSelectClickHandlers(this.select2, this.niceSelect2);
            }, 50);
          } catch (e) {
            console.warn('NiceSelect update failed for select2:', e);
          }
        } else if (this.select3 && nextSelect === this.select3) {
          try {
            this.niceSelect3?.update();
            // Manually update the rendered options immediately
            this.updateNiceSelectOptions(this.select3, this.niceSelect3);
            // Re-setup click handlers after update (NiceSelect may have rebuilt the dropdown)
            setTimeout(() => {
              this.setupNiceSelectClickHandlers(this.select3, this.niceSelect3);
            }, 50);
          } catch (e) {
            console.warn('NiceSelect update failed for select3:', e);
          }
        }
      });
    }
  }

  customElements.define('quick-filter', QuickFilter);
}